import { useVerbiageQuery } from "@repo/baseCloud";
import { MvcLocation, RepFieldAuditHistory } from "@repo/models";
import { filterPositionsWithinSquare, Marker } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { FC, useMemo } from "react";

import { useLocationApi } from "../../api/locationApi";
import * as queries from "../../api/queries";
import { useRepFieldAuditQueries } from "../../api/repFieldAuditApi";
import { useLocationContext } from "../../contexts/editableItemContext";
import { defaultPinImage, mapObjToMarker, PinStyle } from "./markers";
import { PinMap, usePinFilter } from "./pinMap";

enum PinTypes {
	ThisLocation,
	OtherLocation,
	SiteVisit,
}

const pinStyles: Record<PinTypes, PinStyle> = {
	[PinTypes.ThisLocation]: { color: "#ef4444", image: defaultPinImage, ordinal: 1, scale: 2 },
	[PinTypes.OtherLocation]: { color: "#1233CC", image: defaultPinImage, ordinal: 2, scale: 1 },
	[PinTypes.SiteVisit]: { color: "#22c55e", image: defaultPinImage, ordinal: 3, scale: 1 },
};

interface MappedLocation extends MvcLocation {
	id: number;
	code: string;
	pinType: PinTypes;
}

interface MappedSiteVisit extends RepFieldAuditHistory {
	name: string;
	pinType: PinTypes;
}

function mapLocationToMarker(location: MappedLocation): Marker {
	return mapObjToMarker(location, pinStyles[location.pinType]);
}

function mapSiteVisitToMarker(visit: MappedSiteVisit, visitLabel: string): Marker {
	const marker = mapObjToMarker(visit, pinStyles[visit.pinType]);
	return {
		...marker,
		datetime: visit.createdOn,
		tooltip: `${visitLabel} (${visit.user})`,
		label: [{ Name: visit.user, Comments: visit.comments }],
	};
}

export const SiteVisitMap: FC = () => {
	const { getAllLocations } = useLocationApi();
	const { useAllFieldAuditHistoriesQuery } = useRepFieldAuditQueries();
	const { item: location } = useLocationContext();
	const { verbiage } = useVerbiageQuery();
	const filter = usePinFilter<PinTypes>([PinTypes.ThisLocation, PinTypes.SiteVisit]);

	const locationsQuery = useQuery({ queryKey: [queries.GROUP.ALL_LOCATIONS], queryFn: getAllLocations });
	const { data: historyData, loaded: historyLoaded } = useAllFieldAuditHistoriesQuery();

	// These memos keep `markers` stable: MapChart re-places every pin and refits the bounds when it changes
	const locations = useMemo<MappedLocation[]>(() => {
		const nearby = filterPositionsWithinSquare(location.lat, location.lon, locationsQuery.data ?? [], 100);
		return nearby.map((l) => ({
			...l,
			id: l.locationId,
			code: l.locationCode,
			pinType: location.locationId === l.locationId ? PinTypes.ThisLocation : PinTypes.OtherLocation,
		}));
	}, [locationsQuery.data, location]);

	const mappedLocations = useMemo(() => {
		return locations.filter((pos) => filter.isActive(pos.pinType)).map(mapLocationToMarker);
	}, [locations, filter]);

	const showSiteVisits = filter.isActive(PinTypes.SiteVisit);
	const siteVisits = useMemo(() => {
		if (!showSiteVisits) return [];
		return (
			historyData
				?.filter((h) => h.lat && h.lon)
				.map((h) =>
					mapSiteVisitToMarker({ ...h, name: h.user, pinType: PinTypes.SiteVisit }, verbiage.fieldAudit),
				) ?? []
		);
	}, [historyData, showSiteVisits, verbiage.fieldAudit]);

	const markers = useMemo(() => [...mappedLocations, ...siteVisits], [mappedLocations, siteVisits]);

	return (
		<PinMap
			id="site-visit-map"
			loading={!location || locationsQuery.isLoading || !historyLoaded}
			markers={markers}
			pinStyles={pinStyles}
			filter={filter}
			legend={[
				{ pinType: PinTypes.ThisLocation, text: `${location.name} (${location.locationCode})` },
				{ pinType: PinTypes.OtherLocation, text: "Other " + verbiage.location + "s" },
				{ pinType: PinTypes.SiteVisit, text: "Site Visit Positions" },
			]}
		/>
	);
};
